import { isEqual } from 'lodash-es'

export const SEARCH_TYPE = {
  SEARCH_WITHIN: 'SEARCH_WITHIN',
  LOCATION: 'LOCATION',
  TEXT: 'TEXT',
}
export const SEARCH_FILTER_TYPES = {
  TYPE: 'type',
  DATE: 'date',
  EXACT: 'exact',
  CERTIFICATION: 'cert',
  SEARCH_WITHIN: 'search-within',
}

function deepDedupArray(arr) {
  return arr.reduce((result, item) => {
    if (!result.find((existingItem) => isEqual(existingItem, item))) {
      result.push(item)
    }
    return result
  }, [])
}

export function uriDecodeCheck(query) {
  if (query?.match(/%[a-fA-F0-9]{2}/g)) {
    const decoded = decodeURIComponent(query)
    return uriDecodeCheck(decoded)
  }
  return query
}

// Function to parse the hash
export function parseHash(hash) {
  const params = new URLSearchParams(hash?.substr?.(1)) // Remove the '#' at the start
  return Array.from(params.entries()).reduce((obj, [key, value]) => {
    try {
      obj[key] = JSON.parse(decodeURIComponent(value))
    } catch (e) {
      obj[key] = decodeURIComponent(value)
    }
    return obj
  }, {})
}
export function filtersParamToFilterState(filtersParam) {
  if (!filtersParam) return []
  const filters = JSON.parse(uriDecodeCheck(filtersParam))
  if (Array.isArray(filters)) {
    return deepDedupArray(filters)
  }
  return []
}

export function createExactFilter({ exact, selected = false }) {
  return { type: SEARCH_FILTER_TYPES.EXACT, exact, selected }
}

export function createPlaceTypeFilter({ placeType, selected = false }) {
  return { type: SEARCH_FILTER_TYPES.TYPE, placeType, selected }
}

export function createDateFilter({ reqYears, selected = false }) {
  return { type: SEARCH_FILTER_TYPES.DATE, reqYears, selected }
}

export function createCertificationFilter({ certificationLevel, selected = false }) {
  return { type: SEARCH_FILTER_TYPES.CERTIFICATION, certificationLevel, selected }
}

export function createSearchWithinFilter({ reqParents, primaryText, directChildrenOnly, selected = false }) {
  return {
    type: SEARCH_FILTER_TYPES.SEARCH_WITHIN,
    reqParents,
    primaryText,
    directChildrenOnly,
    selected,
  }
}
export function createSearchWithinLabel(newFilter, t) {
  return newFilter.directChildrenOnly
    ? t('search-filter.direct-children-within-parent.label', 'Direct Children of {parent}', {
        parent: newFilter.primaryText,
      })
    : t('search-filter.within-parent.label', 'Within {parent}', { parent: newFilter.primaryText })
}

/* ***** Chip management ****** */
export function createChipParams({ filter: newFilter, t }) {
  let filter
  switch (newFilter.type) {
    case SEARCH_FILTER_TYPES.EXACT: {
      filter = createExactFilter(newFilter)
      const label = t('search-filter.exact.label', 'Exact')
      return { label, value: 'exact', filter, selected: filter.selected }
    }
    case SEARCH_FILTER_TYPES.TYPE:
      filter = createPlaceTypeFilter(newFilter)
      return {
        label: filter.placeType.primaryText,
        value: `${filter.placeType.primaryText}-${filter.placeType.id}`,
        filter,
        selected: filter.selected,
      }
    case SEARCH_FILTER_TYPES.DATE:
      filter = createDateFilter(newFilter)
      return { label: filter.reqYears, value: `${filter.type}-${filter.reqYears}`, filter, selected: filter.selected }
    case SEARCH_FILTER_TYPES.CERTIFICATION: {
      filter = createCertificationFilter(newFilter)
      const certificationLevel = filter.certificationLevel
      const translationKey = `search-filter.confidence-level.${certificationLevel}`
      const label = t(translationKey, certificationLevel)
      return { label, value: certificationLevel, filter, selected: filter.selected }
    }
    case SEARCH_FILTER_TYPES.SEARCH_WITHIN: {
      filter = createSearchWithinFilter(newFilter)
      const { directChildrenOnly, selected } = newFilter
      const label = createSearchWithinLabel(newFilter, t)
      return {
        label,
        value: `${filter.primaryText}-${filter.reqParents}-${directChildrenOnly ? 'D' : ''}${selected ? 'S' : ''}`,
        filter,
        selected: filter.selected,
      }
    }
    default:
      filter = null
      break
  }

  return null
}

export function filterChips(type, chips = []) {
  return chips.filter((chip) => chip.filter.type === type)
}

export function filterChipsCollection(chipsCollection, type) {
  const { chips = [], selectedChips = [] } = chipsCollection
  const typeChips = filterChips(type, chips)
  const selectedTypeChips = filterChips(type, selectedChips)
  return { ...chipsCollection, chips: [...typeChips], selectedChips: [...selectedTypeChips] }
}

function removeOutDatedChips({ removeChip, t, newFilters, oldFilters }) {
  oldFilters?.forEach((oldFilter) => {
    if (!newFilters.some((newFilter) => isEqual(oldFilter, newFilter))) {
      const chip = createChipParams({ filter: oldFilter, t })
      removeChip(chip)
    }
  })
}

function addNewChips({ addChip, t, newFilters, oldFilters }) {
  newFilters?.forEach((newFilter) => {
    if (!oldFilters.some((oldFilter) => isEqual(oldFilter, newFilter))) {
      const chip = createChipParams({ filter: newFilter, t })
      addChip(chip)
    }
  })
}

export function updateChipsFromFilters({ addChip, removeChip, t, newFilters, oldFilters }) {
  removeOutDatedChips({ removeChip, t, newFilters, oldFilters })
  addNewChips({ addChip, t, newFilters, oldFilters })
}

function normalizeString(string) {
  return string.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

function findStringInText(searchString, resultString) {
  if (searchString && resultString) {
    const normalizedResult = normalizeString(resultString)
    const wordsInSearch = searchString.split(/[,\s]+/) // Split by comma or whitespace
    const filteredWords = wordsInSearch.map((word) => normalizeString(word)).filter((word) => word.length > 2)
    return filteredWords.some((word) => normalizedResult.toLowerCase().includes(word.toLowerCase()))
  }
  return false
}

function resultPassesTextFilter({ text, result }) {
  const { place, primaryText, secondaryText, tertiaryText } = result ?? {}
  return (
    !text ||
    findStringInText(text, primaryText) ||
    findStringInText(text, secondaryText) ||
    findStringInText(text, tertiaryText) ||
    (!Number.isNaN(Number(text)) && Number(text) === place.id)
  )
}

function clientSideResultSearchWithinFilter({ filter, result }) {
  const { place } = result ?? {}
  const reqParents = Number(filter.reqParents)
  const reqDirParents = Number(filter.reqDirParents)
  return (
    (!reqParents ||
      (place?.jurisdictionChain?.length > 0 && place.jurisdictionChain.some((id) => id === reqParents))) &&
    (!reqDirParents ||
      (place?.jurisdictionChain?.length > 0 && place.jurisdictionChain.some((id) => id === reqDirParents)))
  )
}

const clientFilters = {
  [SEARCH_FILTER_TYPES.SEARCH_WITHIN]: clientSideResultSearchWithinFilter,
}

function resultPassesFilters({ filters, result }) {
  return (
    filters?.length === 0 ||
    filters.some((filter) => {
      const filterFn = clientFilters[filter.type]
      return !filterFn || filterFn({ filter, result })
    })
  )
}

export function getInternalResultsClientFilterFn({ filters, text }) {
  const filterTypes = Object.values(SEARCH_FILTER_TYPES)
  if (!filters.length) {
    return () => true
  }
  return (result) => {
    return (
      resultPassesTextFilter({ text, result }) &&
      filterTypes.every((type) => {
        const typeFilters = filters.filter(({ type: filterType }) => filterType === type)
        return resultPassesFilters({ filters: typeFilters, result })
      })
    )
  }
}
